import { PAID_TIERS } from '@/store/subscriptions/subscriptions.types';
import type {
  BillingInterval,
  PaidSubscriptionTier,
  Price,
  Subscription,
  SubscriptionTier,
} from '@/store/subscriptions/subscriptions.types';

import { isPaidPlan } from './subscription.display';

/**
 * What a vessel is actually charged, priced from `GET /subscriptions/prices` rather than
 * the mocked `PRICE` table the seat modals still use. Both a saved subscription and a
 * draft the checkout form holds are priced the same way — see `PricedPlan`.
 */

/**
 * Stripe price lookup keys, exactly as the server builds them for checkout —
 * `desiredSeatItems` in the People service's `internal/app/payments/helpers.go`:
 *
 *   seats → `<tier>_<interval>`   e.g. `navigate_monthly`, `command_annually`
 *   crew  → `crew_<interval>`     e.g. `crew_monthly`
 *
 * The crew add-on is priced per interval only — it carries no tier. These are a different
 * namespace from the `navigate_seat` / `command_crew` values in the spec, which are the
 * `seat_type` a person occupies, not a price.
 */
export const seatLookupKey = (tier: PaidSubscriptionTier, interval: BillingInterval) =>
  `${tier}_${interval}`;

export const crewLookupKey = (interval: BillingInterval) => `crew_${interval}`;

const isPaidTier = (tier: SubscriptionTier | null): tier is PaidSubscriptionTier =>
  !!tier && (PAID_TIERS as readonly SubscriptionTier[]).includes(tier);

/** The part of a plan that decides its price — a live subscription's, or a form draft's. */
export interface PricedPlan {
  tier: PaidSubscriptionTier;
  interval: BillingInterval;
  seats: number;
  crewSeats: number;
}

/** The plan a subscription is billed on, or `null` when there is nothing to price. */
export const pricedPlan = (subscription: Subscription | null): PricedPlan | null => {
  if (!isPaidPlan(subscription) || !isPaidTier(subscription.tier) || !subscription.interval) {
    return null;
  }

  const { tier, interval, seats, crewSeats } = subscription;
  return { tier, interval, seats, crewSeats };
};

/** One priced line of the bill: a unit price to look up, and how many of it are held. */
type BillLine = { key: string; quantity: number };

const billLines = ({ tier, interval, seats, crewSeats }: PricedPlan): BillLine[] => {
  const lines = [{ key: seatLookupKey(tier, interval), quantity: seats }];
  // The crew add-on is only priced when the vessel holds any.
  if (crewSeats > 0) lines.push({ key: crewLookupKey(interval), quantity: crewSeats });
  return lines;
};

/** The keys a plan's bill is priced from. */
export const planLookupKeys = (plan: PricedPlan): string[] => billLines(plan).map(({ key }) => key);

/** The keys to ask the endpoint for. Empty for the free plan, which needs no prices. */
export const billLookupKeys = (subscription: Subscription | null): string[] => {
  const plan = pricedPlan(subscription);
  return plan ? planLookupKeys(plan) : [];
};

/**
 * Every key the checkout form can need at one interval: a seat price per purchasable
 * tier, plus the crew add-on. Asked for together so switching tier or stepping crew
 * re-prices from what is already in cache instead of waiting on another request.
 */
export const quoteLookupKeys = (interval: BillingInterval): string[] => [
  ...PAID_TIERS.map((tier) => seatLookupKey(tier, interval)),
  crewLookupKey(interval),
];

/** A single unit price, or `null` when the endpoint did not answer with it. */
export const unitPrice = (prices: Price[] | undefined, key: string): Price | null =>
  prices?.find(({ lookupKey }) => lookupKey === key) ?? null;

/**
 * The charge for one billing period: each seat kind's unit price times how many are held.
 * `null` when a price the bill needs has not come back — a partial sum would understate
 * it, and a wrong figure is worse than none.
 */
export const planBill = (
  plan: PricedPlan,
  prices: Price[] | undefined,
): { amount: number; currency: string } | null => {
  let amount = 0;
  let currency = '';

  for (const line of billLines(plan)) {
    const price = unitPrice(prices, line.key);
    if (!price || Number.isNaN(price.amount)) return null;
    amount += price.amount * line.quantity;
    currency = price.currency;
  }

  return { amount, currency };
};

/** The same charge for a saved subscription. `null` for a plan that has no price at all. */
export const periodBill = (subscription: Subscription | null, prices: Price[] | undefined) => {
  const plan = pricedPlan(subscription);
  return plan && planBill(plan, prices);
};

/**
 * `amount.number` is a decimal string in major units — `"29"`, not Stripe's cents — and
 * `currency` an ISO code. Cents are only shown when the price actually carries them.
 */
export const fmtPrice = (amount: number, currency: string) => {
  // Both bounds move together, so `19.9` reads `$19.90`, not `$19.9`.
  const digits = Number.isInteger(amount) ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: /^[a-z]{3}$/i.test(currency) ? currency.toUpperCase() : 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
};
